import { useState } from "react";
import { AlertTriangle, Loader2 } from "lucide-react";

/* Permanent trader deletion. The admin must type the trader's email to arm the button. */
export const DeleteTraderDialog = ({ open, trader, busy, onConfirm, onClose }) => {
  const [typed, setTyped] = useState("");
  if (!open || !trader) return null;
  const armed = typed.trim().toLowerCase() === String(trader.email || "").toLowerCase();

  return (
    <div className="fixed inset-0 z-[95] flex items-end sm:items-center justify-center p-0 sm:p-6">
      <button
        type="button"
        aria-label="Cancel"
        data-testid="delete-trader-backdrop"
        onClick={busy ? undefined : onClose}
        className="absolute inset-0 bg-black/75"
      />
      <div
        data-testid="delete-trader-dialog"
        className="relative w-full sm:max-w-lg border border-red-900/70 bg-[#0B0C0D] rounded-t-2xl sm:rounded-sm p-5 shadow-2xl"
      >
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
          <div className="min-w-0">
            <h3 className="font-heading text-base font-bold tracking-tight text-zinc-50">
              Delete this trader permanently
            </h3>
            <p className="mt-1 text-xs text-zinc-400 leading-relaxed">
              This wipes the account and everything attached to it — trades, closed deals, alerts,
              sessions, their MT5 link and rule overrides — and frees the pod slot. It cannot be
              undone. Use <span className="text-zinc-200">Remove</span> instead if you only want to
              revoke access.
            </p>
          </div>
        </div>

        <p className="mt-4 font-mono text-[11px] text-zinc-400">
          Type <span className="text-red-300">{trader.email}</span> to confirm
        </p>
        <input
          autoFocus
          data-testid="delete-trader-confirm-input"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder={trader.email}
          className="mt-1.5 w-full rounded-sm bg-zinc-900/60 border border-zinc-800 px-3 py-2 font-mono text-sm text-zinc-100 focus:border-red-500 outline-none transition-colors duration-200"
        />

        <div className="mt-4 flex items-center gap-2">
          <button
            type="button"
            data-testid="delete-trader-cancel-btn"
            disabled={busy}
            onClick={onClose}
            className="h-10 flex-1 rounded-sm border border-zinc-600 text-[11px] font-bold uppercase tracking-[0.12em] text-zinc-300 transition-colors duration-200 disabled:opacity-40"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="delete-trader-confirm-btn"
            disabled={busy || !armed}
            onClick={() => onConfirm(typed.trim())}
            className="h-10 flex-[1.3] rounded-sm bg-red-600 hover:bg-red-500 text-white text-[11px] font-bold uppercase tracking-[0.12em] transition-colors duration-200 active:scale-95 disabled:opacity-40"
          >
            {busy ? (
              <Loader2 className="mx-auto h-4 w-4 animate-spin" />
            ) : (
              "Delete everything"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
